// AgentExperienceShell.jsx — m3-06a two-mode shell (Standard ⇄ AI Agent).
//
// m3-nav-morph FIX §1:
//   - .ax-desk is NO LONGER position:fixed;inset:0 — it is a fixed element offset by
//     --vs-nav-w (set by the nav CSS + JS). The nav.sidebar is the ONLY sidebar.
//   - The AgentStudioChat component no longer renders .st-side (deleted there too).
//   - Mode flip: handled client-side in the nav JS + VsNavLower.jsx. The shell listens
//     for the 'vesta:nav:mode-change' window event and updates its own mode state.
//
// ONE app, two layouts (DESIGN-INTEGRATION §2.2, docs/01):
//   - Standard : the Activity destination full-page (the gradient nav is the server-rendered
//                HAML sidebar that's already on the page; React only owns the content area).
//   - Agent    : the Codex-desktop "Agent Studio" — conversation + resizable right panel
//                that mounts the SAME Activity surface (menu-less). Nav owns the chat rail.
//
// Mode is driven by the nav toggle (client-side + URL) and read from the page's data-mode
// attribute (server-rendered), with localStorage fallback for continuity (modeResolver).

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  resolveMode,
  isAgentMode,
  persistMode,
  readPersistedMode,
  persistActivityView,
  readPersistedActivityView,
} from "./modeResolver"
import { buildActivityViewModel } from "./activityViewModel"
import ActivitySurface from "./ActivitySurface.jsx"
import ActivityTabs from "./ActivityTabs.jsx"
import AgentStudioChat from "./AgentStudioChat.jsx"
import { askFromProgress, litTaskFor } from "./askVestaHandoff.js"
import { consumeLaunch } from "./libraryLaunch.js"
// Item #7: the library back button re-stages a {chatId, activityView} RESTORE descriptor and
// navigates here; the shell consumes it on mount and restores the exact pre-View-all state.
import { consumeRestore } from "./libraryReturn.js"
import {
  SUGGESTIONS_TWO_COLUMN_MIN_WIDTH,
  clampPanelWidth,
  panelWidthForChat,
} from "./agentLayout.js"

const safeStorage = () => {
  try { return window.localStorage } catch (_e) { return null }
}

function AgentExperienceShell({ companySlug, dataMode }) {
  const [searchParams] = useSearchParams()
  const storage = safeStorage()

  // ── mode ──────────────────────────────────────────────────────────────────
  // Priority: explicit ?mode= → server data-mode → persisted → default.
  const requestedMode = searchParams.get("mode") || dataMode || null
  const [mode, setMode] = useState(() =>
    resolveMode(requestedMode, readPersistedMode(storage))
  )

  // Re-resolve when the URL search param changes (the nav toggle navigates).
  useEffect(() => {
    const next = resolveMode(searchParams.get("mode") || dataMode || null, readPersistedMode(storage))
    setMode(next)
  }, [searchParams, dataMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // m3-nav-morph: also listen for the nav's client-side mode-change event (D-E).
  useEffect(() => {
    function onNavModeChange(e) {
      const next = resolveMode(e.detail.mode, readPersistedMode(storage))
      setMode(next)
    }
    window.addEventListener("vesta:nav:mode-change", onNavModeChange)
    return () => window.removeEventListener("vesta:nav:mode-change", onNavModeChange)
  }, [storage]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the resolved mode for continuity (refresh / back-forward).
  useEffect(() => { persistMode(mode, storage) }, [mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── activity tab (lifted; mirrors site ⇄ panel; persists across mode switches) ──
  const [activityView, setActivityView] = useState(() =>
    readPersistedActivityView(storage) || "board"
  )
  const onSelectView = useCallback((v) => {
    setActivityView(v)
    persistActivityView(v, storage)
  }, [storage])

  // ── m3-15 board → chat "Ask Vesta" handoff (shell holds the state) ──
  const [askRef, setAskRef] = useState(null)
  const [composerSeed, setComposerSeed] = useState("")
  // Bumped each time the drawer fires a handoff — signals the chat to auto-SEND the question
  // (with the task attached) as a real thread turn, not just seed the composer.
  const [sendNonce, setSendNonce] = useState(0)
  // litTask + the composer pill derive from ONE source (askRef) so the board card glow and the
  // composer glow can never disagree (askVestaHandoff.litTaskFor).
  const litTask = litTaskFor(askRef)

  // The canonical handoff: a board card's drawer footer calls this. Enter agent mode, attach the
  // task pill, light the source card, seed the composer. (askFromProgress is pure.)
  const handleAsk = useCallback((text, taskRef) => {
    const next = askFromProgress({ mode, askRef }, text, taskRef)
    if (!next) return
    setMode(next.mode)            // enter agent mode
    setAskRef(next.askRef)        // pill + lit card (one source)
    setComposerSeed(next.composerSeed)
    setSendNonce((n) => n + 1)    // tell the chat to auto-send this question as a turn
  }, [mode, askRef])

  const clearRef = useCallback(() => { setAskRef(null); setComposerSeed("") }, [])

  // ── Task 9 D-T9-g: cross-page library launch (library page → this shell) ──
  // A library CTA stashed a descriptor in sessionStorage and navigated here (/activity?mode=agent).
  // On mount, consume + CLEAR it (a back/forward can't re-fire):
  //   job → enter agent mode, switch to the Agent Jobs tab, stash the jobSeed → surfaceProps →
  //         TriggeredTasksList.seedJob → NewAgentForm opens prefilled.
  // Item #6 unification: the old `task` branch (auto-send the prompt as a chat turn) is RETIRED —
  // launchDescriptor now emits every entry as a job descriptor (quick tasks seed the same full
  // form with kind:'chat'), so nothing stages a task descriptor any more. A stale task descriptor
  // from a pre-deploy tab simply no-ops here (consumed + cleared, no crash).
  const [librarySeed, setLibrarySeed] = useState(null)
  useEffect(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    const desc = consumeLaunch(store)
    if (!desc) return
    if (desc.kind === "job" && desc.jobSeed) {
      setMode("agent")
      onSelectView("agents")
      setLibrarySeed(desc.jobSeed)
    }
  }, []) // mount-once — eslint-disable-line react-hooks/exhaustive-deps

  // ── Item #7: library back-button RESTORE (the return half of the View-all round trip) ──
  // Consume + CLEAR on mount (a back/forward can never re-fire). Restores the right-panel view via
  // onSelectView, then — ONLY when a chat was open at View-all time — re-dispatches the FROZEN
  // id-only 'vesta:nav:select-chat' contract (VsNavLower.jsx §7; programmatic precedent:
  // TriggeredTasksList's review-chat seed). Deliberately does NOT emit the 'vesta:nav:chat-selected'
  // meta companion — that channel is additive meta only; select-chat is what mounts the chat.
  // Timing: AgentStudioChat is rendered by this same first commit (mode resolves 'agent' from the
  // URL) and child effects run before parent effects, so its select-chat listener is registered
  // before this dispatch fires.
  useEffect(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    const restore = consumeRestore(store)
    if (!restore) return
    setMode("agent")
    onSelectView(restore.activityView)
    if (restore.chatId != null) {
      window.dispatchEvent(new CustomEvent("vesta:nav:select-chat", { detail: { id: String(restore.chatId) } }))
    }
  }, []) // mount-once — eslint-disable-line react-hooks/exhaustive-deps

  // ── panel resize state (agent mode) ─────────────────────────────────────────
  // Always start null. useLayoutEffect (below) fires before the browser paints
  // and gives Suggestions the exact 641px needed for its two-column layout,
  // while preserving the live panel's 280px floor on narrower bodies.
  const [panelWidth, setPanelWidth] = useState(null)

  const axBodyRef = useRef(null)
  const agent = isAgentMode(mode)

  // Area 5 — auto-widen: solve the split from the requested chat width while
  // keeping the panel floor. SuggestionDock requests the shared 641px boundary.
  const requestChatWidth = useCallback((desiredChatWidth) => {
    const body = axBodyRef.current
    const bodyWidth = body ? body.getBoundingClientRect().width : window.innerWidth
    setPanelWidth(panelWidthForChat(bodyWidth, desiredChatWidth))
  }, [])

  // Reset to the two-column Suggestions boundary on EVERY agent-mode entry,
  // BEFORE the browser paints.
  // useLayoutEffect fires synchronously after DOM mutations but before paint — frame-one
  // is already correct. No RAF needed (and RAF would be one frame late).
  // Guard: if .ax-body has zero width (not yet in layout), fall through to null so
  // panelStyle uses the thin-biased flexBasis fallback ("46%") on that edge case.
  useLayoutEffect(() => {
    if (!agent) return
    const body = axBodyRef.current
    const bodyWidth = body ? body.getBoundingClientRect().width : window.innerWidth
    if (bodyWidth > 0) {
      setPanelWidth(panelWidthForChat(bodyWidth, SUGGESTIONS_TWO_COLUMN_MIN_WIDTH))
    }
    // else: panelWidth stays null → panelStyle falls back to flexBasis:"46%" (thin side)
  }, [agent]) // eslint-disable-line react-hooks/exhaustive-deps

  // Window resize keeps manual splits legal. On very narrow bodies the panel
  // floor wins, because both minimums cannot fit simultaneously.
  useEffect(() => {
    if (!agent) return
    const onWinResize = () => {
      const body = axBodyRef.current
      const bodyWidth = body ? body.getBoundingClientRect().width : window.innerWidth
      setPanelWidth((w) => {
        const legalWidth = clampPanelWidth(bodyWidth, w)
        return w > legalWidth ? legalWidth : w
      })
    }
    window.addEventListener("resize", onWinResize)
    return () => window.removeEventListener("resize", onWinResize)
  }, [agent]) // eslint-disable-line react-hooks/exhaustive-deps

  // THE SINGLE SOURCE: one view-model call. In agent mode the panel renders it; in standard
  // mode the page renders it. Same descriptor → same surface + data contract.
  const vmPage = useMemo(
    () => buildActivityViewModel({
      activeView: activityView,
      storedView: readPersistedActivityView(storage),
      placement: "page",
      companySlug,
      litTask,
      onAsk: handleAsk,
      seedJob: librarySeed,
    }),
    [activityView, companySlug, litTask, handleAsk, librarySeed] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const vmPanel = useMemo(
    () => buildActivityViewModel({
      activeView: activityView,
      storedView: readPersistedActivityView(storage),
      placement: "panel",
      companySlug,
      litTask,
      onAsk: handleAsk,
      seedJob: librarySeed,
    }),
    [activityView, companySlug, litTask, handleAsk, librarySeed] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ── host-controlled segmented tabs (m3-11 ActivityTabs; sticky/blur on page, plain in panel) ──
  // The shell owns the active tab (`activityView`); ActivityTabs is presentation only.

  // ── STANDARD layout ─────────────────────────────────────────────────────────
  if (!agent) {
    return (
      <div className="ax-shell ax-shell--standard">
        <ActivityTabs active={vmPage.view} placement="page" onChange={onSelectView} />
        <ActivitySurface vm={vmPage} />
      </div>
    )
  }

  // ── AGENT layout (Agent Studio desk) ────────────────────────────────────────
  // Chat column placeholder (m3-13) on the left; the live Activity panel on the right.
  const panelStyle = panelWidth ? { flex: `0 0 ${panelWidth}px` } : { flexBasis: "46%" }

  const onResize = (startX, startWidth, bodyWidth) => (e) => {
    const dx = startX - e.clientX
    setPanelWidth(clampPanelWidth(bodyWidth, startWidth + dx))
  }

  const beginResize = (e) => {
    e.preventDefault()
    const body = e.currentTarget.closest(".ax-body")
    const bodyWidth = body ? body.getBoundingClientRect().width : window.innerWidth
    const startWidth = panelWidth || Math.round(bodyWidth * 0.46)
    const move = onResize(e.clientX, startWidth, bodyWidth)
    const up = () => {
      window.removeEventListener("mousemove", move)
      window.removeEventListener("mouseup", up)
    }
    window.addEventListener("mousemove", move)
    window.addEventListener("mouseup", up)
  }

  // m3-nav-morph: ax-desk is now position:fixed offset by --vs-nav-w (see navbars.scss §1 fix).
  // It no longer owns a sidebar; the ONE nav.sidebar recolors via [data-agent-mode].
  return (
    <div className="ax-desk">
      <div className="ax-body" ref={axBodyRef}>
        {/* chat column — m3-13 Agent Studio chat shell (sidebar + conversation + composer) */}
        <div className="ax-mid">
          <AgentStudioChat companySlug={companySlug} askRef={askRef} composerSeed={composerSeed} sendNonce={sendNonce} onClearRef={clearRef} onRequestChatWidth={requestChatWidth} />
        </div>

        {/* resize handle */}
        <div
          className="ax-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onMouseDown={beginResize}
        />

        {/* right panel — the live Vesta app (menu-less), same Activity surface */}
        <section className="ax-panel" style={panelStyle} aria-label="Live app">
          <div className="ax-panel__chrome">
            <ActivityTabs active={vmPanel.view} placement="panel" onChange={onSelectView} />
          </div>
          <div className="ax-app-scroll">
            <ActivitySurface vm={vmPanel} />
          </div>
        </section>
      </div>
    </div>
  )
}

export default AgentExperienceShell
